// Journey summary PDF — everything the employee saw during onboarding (systems,
// department Q&A, checklist) so they have it for reference and it can be
// attached to the completion email.
//
// Arabic text is drawn with Canvas 2D fillText (correct shaping/bidi come free
// from the browser's own text layout engine — the same technique already used
// for the certificate PNG) and each finished page is embedded into the PDF as
// an image via jsPDF. This sidesteps jsPDF's own text renderer, which has no
// real Arabic shaping support. URLs are always drawn LTR (correct even inside
// RTL paragraphs) and get real jsPDF link annotations laid over their drawn
// position so they're clickable in the finished PDF, not just printed text.
// App-store links also get a real, offline-generated QR code (the `qrcode`
// package — no network call, unlike the site's own <QRCode> which hits a
// third-party API and isn't safe to rely on during PDF/email generation).
//
// Layout is a branded report: a repeated header band with the Kaden logo on
// every page, a bordered "employee details" panel instead of bare label/value
// stacks, accent-barred section headings, and a footer with a confidentiality
// line + page numbers.

import jsPDF from "jspdf";
import QRCode from "qrcode";
import type { Lang } from "./i18n";
import type { UserProfile } from "./auth";
import type { AppIntegration } from "../data/apps";
import type { QAItem } from "../data/qa";
import type { ChecklistItem } from "../data/checklist";
import { systemName, systemCategory, systemDesc, systemAccess } from "../data/systems";

const GOLD = "#dab87a";
const DARK = "#1a2820";
const GREEN = "#2d7a4a";
const MUTED = "#5a7060";
const BODY = "#2a2f2c";
const CREAM = "#f4efe4";
const FONT = "'Cairo','Outfit',sans-serif";

// Canvas px dims match the A4 ratio (210:297) so jsPDF can map 1:1 onto an A4 page.
const PAGE_W = 1000;
const PAGE_H = 1414;
const MARGIN = 72;
const CONTENT_W = PAGE_W - MARGIN * 2;
const QR_SIZE = 130;
const HEADER_H = 108;
const FOOTER_H = 52;
const TOP_START = HEADER_H + 30;
const BOTTOM_LIMIT = PAGE_H - FOOTER_H - 20;

interface LinkRegion { x: number; y: number; w: number; h: number; url: string }
interface QrItem { label: string; url: string }
interface KvPair { label: string; value: string }

type Block =
  | { type: "h1"; text: string }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "p"; text: string }
  | { type: "bullet"; text: string }
  | { type: "link"; label?: string; url: string }
  | { type: "profile-box"; title: string; pairs: KvPair[] }
  | { type: "qr-row"; items: QrItem[] }
  | { type: "divider" }
  | { type: "spacer"; h?: number };

/** Strip the small HTML subset used in Q&A answers down to plain paragraph/bullet lines. */
function htmlToBlocks(html: string): Block[] {
  const container = document.createElement("div");
  container.innerHTML = html || "";
  const out: Block[] = [];
  const clean = (s: string | null) => (s || "").replace(/\s+/g, " ").trim();
  container.childNodes.forEach((node) => {
    const el = node as HTMLElement;
    const tag = el.nodeName?.toLowerCase();
    if (tag === "ul" || tag === "ol") {
      el.querySelectorAll("li").forEach((li) => {
        const text = clean(li.textContent);
        if (text) out.push({ type: "bullet", text });
      });
    } else if (tag === "p" || tag === "div") {
      const text = clean(el.textContent);
      if (text) out.push({ type: "p", text });
    } else {
      const text = clean(node.textContent);
      if (text) out.push({ type: "p", text });
    }
  });
  return out;
}

function buildBlocks(opts: {
  lang: Lang;
  profile: UserProfile | null;
  careerPathLabel: string;
  date: string;
  durationLabel?: string;
  systems: AppIntegration[];
  qa: QAItem[];
  checklist: ChecklistItem[];
  pendingIds?: string[];
}): Block[] {
  const { lang, profile, careerPathLabel, date, durationLabel, systems, qa, checklist, pendingIds } = opts;
  const pendingSet = new Set(pendingIds || []);
  const t = (ar: string, en: string) => (lang === "ar" ? ar : en);
  const blocks: Block[] = [];

  blocks.push({ type: "h1", text: t("ملخّص رحلة التأهيل الرقمي", "Digital Onboarding Summary") });
  blocks.push({ type: "spacer", h: 18 });

  const pairs: KvPair[] = [
    { label: t("الاسم", "Name"), value: profile?.name || "" },
    { label: t("المسمى الوظيفي", "Role"), value: profile?.role || "" },
    { label: t("البريد الإلكتروني", "Email"), value: profile?.email || "" },
    { label: t("المسار المهني", "Career Path"), value: careerPathLabel || "-" },
    { label: t("تاريخ الإتمام", "Completion Date"), value: date },
  ];
  if (durationLabel) pairs.push({ label: t("مدة الرحلة", "Journey Duration"), value: durationLabel });
  blocks.push({ type: "profile-box", title: t("بيانات الموظف", "Employee Details"), pairs });

  blocks.push({ type: "spacer", h: 26 });
  blocks.push({ type: "h2", text: t("الأنظمة والتطبيقات", "Systems & Applications") });
  if (systems.length === 0) {
    blocks.push({ type: "p", text: t("لا توجد أنظمة مرتبطة بهذا المسار.", "No systems linked to this path.") });
  }
  for (const sys of systems) {
    blocks.push({ type: "spacer", h: 10 });
    blocks.push({ type: "h3", text: `${systemName(sys, lang)}, ${systemCategory(sys, lang)}` });
    const desc = systemDesc(sys, lang);
    if (desc) blocks.push({ type: "p", text: desc });
    const access = systemAccess(sys, lang);
    if (access) blocks.push({ type: "bullet", text: `${t("طريقة الوصول", "How to access")}: ${access}` });
    if (sys.url) blocks.push({ type: "link", label: t("فتح النظام", "Open system"), url: sys.url });

    if (sys.hasApp && (sys.ios || sys.android)) {
      const qrItems: QrItem[] = [];
      if (sys.ios) qrItems.push({ label: "App Store", url: sys.ios });
      if (sys.android) qrItems.push({ label: "Google Play", url: sys.android });
      blocks.push({ type: "spacer", h: 4 });
      blocks.push({
        type: "p",
        text: sys.appName ? `${t("تحميل تطبيق", "Download app")} ${sys.appName}` : t("متوفر كتطبيق جوال", "Available as a mobile app"),
      });
      if (sys.ios) blocks.push({ type: "link", label: "App Store", url: sys.ios });
      if (sys.android) blocks.push({ type: "link", label: "Google Play", url: sys.android });
      blocks.push({ type: "qr-row", items: qrItems });
    }
  }

  blocks.push({ type: "spacer", h: 24 });
  blocks.push({ type: "h2", text: t("الأسئلة الشائعة لقسمك", "Department Q&A") });
  if (qa.length === 0) {
    blocks.push({ type: "p", text: t("لا توجد أسئلة مرتبطة بهذا القسم.", "No questions linked to this department.") });
  }
  for (const item of qa) {
    blocks.push({ type: "spacer", h: 10 });
    blocks.push({ type: "h3", text: lang === "ar" ? item.questionAr : item.questionEn });
    const html = lang === "ar" ? item.answerHtmlAr : item.answerHtmlEn;
    blocks.push(...htmlToBlocks(html));
  }

  blocks.push({ type: "spacer", h: 24 });
  blocks.push({ type: "h2", text: t("قائمة المراجعة الأساسية", "Essential Checklist") });
  blocks.push({ type: "spacer", h: 6 });
  for (const item of checklist) {
    const isPending = pendingSet.has(item.id);
    const mark = isPending ? "○" : "✓";
    const suffix = isPending ? ` (${t("لم يكتمل بعد", "not completed yet")})` : "";
    blocks.push({ type: "bullet", text: `${mark} ${lang === "ar" ? item.ar : item.en}${suffix}` });
  }

  return blocks;
}

/** Word-wrap that also hard-breaks a single "word" too long to fit (e.g. a URL). */
function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    if (ctx.measureText(word).width > maxWidth) {
      if (line) { lines.push(line); line = ""; }
      let chunk = "";
      for (const ch of word) {
        const test = chunk + ch;
        if (ctx.measureText(test).width > maxWidth && chunk) {
          lines.push(chunk);
          chunk = ch;
        } else {
          chunk = test;
        }
      }
      line = chunk;
      continue;
    }
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function profileBoxHeight(ctx: CanvasRenderingContext2D, pairs: KvPair[]): number {
  const rows = Math.ceil(pairs.length / 2);
  return 46 + rows * 56 + 24;
}

/** Measure how tall a block will render as (without drawing), for pagination. */
function measureBlock(ctx: CanvasRenderingContext2D, block: Block, qrImages: Map<string, HTMLImageElement>): number {
  switch (block.type) {
    case "h1": return 58;
    case "h2": return 46;
    case "h3": {
      ctx.font = `700 22px ${FONT}`;
      return wrapLines(ctx, block.text, CONTENT_W).length * 30 + 8;
    }
    case "p": {
      ctx.font = `400 18px ${FONT}`;
      return wrapLines(ctx, block.text, CONTENT_W).length * 27;
    }
    case "bullet": {
      ctx.font = `400 18px ${FONT}`;
      return wrapLines(ctx, block.text, CONTENT_W - 28).length * 27;
    }
    case "link": {
      ctx.font = `600 17px ${FONT}`;
      return wrapLines(ctx, block.url, CONTENT_W - 24).length * 26 + 8;
    }
    case "profile-box": return profileBoxHeight(ctx, block.pairs);
    case "qr-row": return block.items.some((i) => qrImages.has(i.url)) ? QR_SIZE + 44 : 0;
    case "divider": return 14;
    case "spacer": return block.h ?? 16;
    default: return 0;
  }
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawBlock(ctx: CanvasRenderingContext2D, block: Block, y: number, rtl: boolean, qrImages: Map<string, HTMLImageElement>): { y: number; links: LinkRegion[] } {
  const x0 = rtl ? PAGE_W - MARGIN : MARGIN;
  const links: LinkRegion[] = [];
  ctx.textAlign = rtl ? "right" : "left";
  ctx.direction = rtl ? "rtl" : "ltr";
  switch (block.type) {
    case "h1": {
      ctx.fillStyle = GREEN;
      ctx.font = `800 36px ${FONT}`;
      ctx.fillText(block.text, x0, y + 30);
      ctx.strokeStyle = GOLD;
      ctx.lineWidth = 2;
      const uw = 64;
      const ux = rtl ? PAGE_W - MARGIN - uw : MARGIN;
      ctx.beginPath();
      ctx.moveTo(ux, y + 46);
      ctx.lineTo(ux + uw, y + 46);
      ctx.stroke();
      return { y: y + 58, links };
    }
    case "h2": {
      // Accent bar + heading, like a report section divider.
      const barW = 5;
      const barX = rtl ? PAGE_W - MARGIN - barW : MARGIN;
      ctx.fillStyle = GOLD;
      ctx.fillRect(barX, y + 4, barW, 26);
      ctx.fillStyle = DARK;
      ctx.font = `800 24px ${FONT}`;
      const tx = rtl ? barX - 12 : barX + barW + 12;
      ctx.fillText(block.text, tx, y + 24);
      ctx.strokeStyle = "#dab87a55";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(MARGIN, y + 40);
      ctx.lineTo(PAGE_W - MARGIN, y + 40);
      ctx.stroke();
      return { y: y + 46, links };
    }
    case "h3": {
      ctx.fillStyle = DARK;
      ctx.font = `700 22px ${FONT}`;
      const lines = wrapLines(ctx, block.text, CONTENT_W);
      let cy = y + 18;
      for (const l of lines) { ctx.fillText(l, x0, cy); cy += 30; }
      return { y: cy - 10, links };
    }
    case "p": {
      ctx.fillStyle = BODY;
      ctx.font = `400 18px ${FONT}`;
      const lines = wrapLines(ctx, block.text, CONTENT_W);
      let cy = y + 16;
      for (const l of lines) { ctx.fillText(l, x0, cy); cy += 27; }
      return { y: cy - 11, links };
    }
    case "bullet": {
      ctx.fillStyle = BODY;
      ctx.font = `400 18px ${FONT}`;
      const lines = wrapLines(ctx, block.text, CONTENT_W - 28);
      let cy = y + 16;
      const bx = rtl ? x0 - 8 : x0 + 8;
      for (let i = 0; i < lines.length; i++) {
        const prefixed = i === 0 ? (rtl ? `${lines[i]} •` : `• ${lines[i]}`) : lines[i];
        ctx.textAlign = rtl ? "right" : "left";
        ctx.fillText(prefixed, i === 0 ? x0 : bx, cy);
        cy += 27;
      }
      return { y: cy - 11, links };
    }
    case "link": {
      // URLs always draw LTR (correct even inside an RTL page) and get a real
      // clickable annotation over their drawn bounding box.
      ctx.fillStyle = GREEN;
      ctx.font = `600 17px ${FONT}`;
      const prefix = block.label ? `${block.label}: ` : "";
      ctx.textAlign = "left";
      ctx.direction = "ltr";
      const lx = rtl ? MARGIN : x0;
      const lines = wrapLines(ctx, prefix + block.url, CONTENT_W - 24);
      let cy = y + 18;
      for (const l of lines) {
        const w = ctx.measureText(l).width;
        ctx.fillText(l, lx, cy);
        ctx.strokeStyle = GREEN;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(lx, cy + 3);
        ctx.lineTo(lx + w, cy + 3);
        ctx.stroke();
        links.push({ x: lx, y: cy - 18, w, h: 24, url: block.url });
        cy += 26;
      }
      return { y: cy - 2, links };
    }
    case "profile-box": {
      const h = profileBoxHeight(ctx, block.pairs);
      roundRect(ctx, MARGIN, y, CONTENT_W, h, 12);
      ctx.fillStyle = "#2d7a4a0d";
      ctx.fill();
      ctx.strokeStyle = "#dab87a55";
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.fillStyle = GREEN;
      ctx.font = `800 16px ${FONT}`;
      ctx.textAlign = rtl ? "right" : "left";
      ctx.fillText(block.title, rtl ? PAGE_W - MARGIN - 22 : MARGIN + 22, y + 30);

      const colW = (CONTENT_W - 44) / 2;
      const col0X = rtl ? PAGE_W - MARGIN - 22 : MARGIN + 22;
      const col1X = rtl ? col0X - colW - 20 : col0X + colW + 20;
      let rowY = y + 68;
      for (let i = 0; i < block.pairs.length; i += 2) {
        for (let c = 0; c < 2; c++) {
          const pair = block.pairs[i + c];
          if (!pair) continue;
          const cx = c === 0 ? col0X : col1X;
          ctx.textAlign = rtl ? "right" : "left";
          ctx.fillStyle = MUTED;
          ctx.font = `600 13px ${FONT}`;
          ctx.fillText(pair.label, cx, rowY);
          ctx.fillStyle = DARK;
          ctx.font = `700 18px ${FONT}`;
          const valMax = colW - 4;
          const valLines = wrapLines(ctx, pair.value || "-", valMax);
          ctx.fillText(valLines[0] || "-", cx, rowY + 24);
        }
        rowY += 56;
      }
      return { y: y + h, links };
    }
    case "qr-row": {
      const present = block.items.filter((i) => qrImages.has(i.url));
      if (present.length === 0) return { y, links };
      const gap = 28;
      const totalW = present.length * QR_SIZE + (present.length - 1) * gap;
      // Centered under the section, regardless of language direction.
      let startX = MARGIN + (CONTENT_W - totalW) / 2;
      for (const item of present) {
        const img = qrImages.get(item.url)!;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(startX, y, QR_SIZE, QR_SIZE);
        ctx.drawImage(img, startX, y, QR_SIZE, QR_SIZE);
        ctx.strokeStyle = "#00000022";
        ctx.strokeRect(startX, y, QR_SIZE, QR_SIZE);
        ctx.fillStyle = MUTED;
        ctx.font = `600 13px ${FONT}`;
        ctx.textAlign = "center";
        ctx.direction = "ltr";
        ctx.fillText(item.label, startX + QR_SIZE / 2, y + QR_SIZE + 20);
        startX += QR_SIZE + gap;
      }
      return { y: y + QR_SIZE + 40, links };
    }
    case "divider": {
      ctx.strokeStyle = GOLD;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(MARGIN, y + 6);
      ctx.lineTo(PAGE_W - MARGIN, y + 6);
      ctx.stroke();
      return { y: y + 14, links };
    }
    case "spacer":
      return { y: y + (block.h ?? 16), links };
    default:
      return { y, links };
  }
}

function newPageCanvas(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement("canvas");
  canvas.width = PAGE_W;
  canvas.height = PAGE_H;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, PAGE_W, PAGE_H);
  return { canvas, ctx };
}

/** Draw the repeated header band + footer (page numbers, confidentiality line)
 *  onto a finished content page. Run as a second pass once the total page
 *  count is known. */
function drawChrome(
  ctx: CanvasRenderingContext2D,
  opts: { lang: Lang; logo: HTMLImageElement | null; pageIndex: number; pageCount: number; generatedOn: string },
) {
  const { lang, logo, pageIndex, pageCount, generatedOn } = opts;
  const rtl = lang === "ar";
  const t = (ar: string, en: string) => (rtl ? ar : en);

  // Header band.
  const grad = ctx.createLinearGradient(0, 0, PAGE_W, 0);
  grad.addColorStop(0, DARK);
  grad.addColorStop(1, GREEN);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, PAGE_W, HEADER_H);
  ctx.fillStyle = GOLD;
  ctx.fillRect(0, HEADER_H - 3, PAGE_W, 3);

  if (logo) {
    const lh = 40;
    const lw = (logo.naturalWidth / logo.naturalHeight) * lh;
    const lx = rtl ? PAGE_W - MARGIN - lw : MARGIN;
    ctx.drawImage(logo, lx, (HEADER_H - lh) / 2 - 4, lw, lh);
  } else {
    ctx.fillStyle = GOLD;
    ctx.font = `800 26px ${FONT}`;
    ctx.textAlign = rtl ? "right" : "left";
    ctx.direction = "ltr";
    ctx.fillText("KADEN", rtl ? PAGE_W - MARGIN : MARGIN, HEADER_H / 2 + 8);
  }

  ctx.fillStyle = CREAM;
  ctx.font = `600 15px ${FONT}`;
  ctx.textAlign = rtl ? "left" : "right";
  ctx.direction = rtl ? "rtl" : "ltr";
  ctx.fillText(t("رحلة التأهيل الرقمي", "Digital Onboarding Journey"), rtl ? MARGIN : PAGE_W - MARGIN, HEADER_H / 2 + 6);

  // Footer.
  ctx.strokeStyle = "#dab87a66";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(MARGIN, PAGE_H - FOOTER_H);
  ctx.lineTo(PAGE_W - MARGIN, PAGE_H - FOOTER_H);
  ctx.stroke();

  ctx.fillStyle = MUTED;
  ctx.font = `500 12px ${FONT}`;
  ctx.textAlign = rtl ? "right" : "left";
  ctx.direction = rtl ? "rtl" : "ltr";
  ctx.fillText(t(`كادن للاستثمار، سرّي، للاستخدام الداخلي · صدر في ${generatedOn}`, `Kaden Investment, Confidential, internal use only · Generated ${generatedOn}`), MARGIN, PAGE_H - FOOTER_H + 26);

  ctx.textAlign = rtl ? "left" : "right";
  ctx.direction = "ltr";
  ctx.fillText(`${pageIndex + 1} / ${pageCount}`, PAGE_W - MARGIN, PAGE_H - FOOTER_H + 26);
}

/** Generate offline QR codes (no network call) for every unique URL referenced
 *  by a qr-row block, preloaded as ready-to-draw Image objects. */
async function preloadQrImages(blocks: Block[]): Promise<Map<string, HTMLImageElement>> {
  const urls = new Set<string>();
  for (const b of blocks) if (b.type === "qr-row") for (const i of b.items) urls.add(i.url);
  const map = new Map<string, HTMLImageElement>();
  await Promise.all(
    [...urls].map(async (url) => {
      try {
        const dataUrl = await QRCode.toDataURL(url, {
          width: QR_SIZE * 2,
          margin: 1,
          color: { dark: "#1a2820", light: "#ffffff" },
        });
        const img = await loadImage(dataUrl);
        map.set(url, img);
      } catch (err) {
        console.warn("[summaryPdf] QR generation failed for", url, err);
      }
    }),
  );
  return map;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load image: ${src}`));
    img.src = src;
  });
}

/** Render the full summary to a multi-page PDF Blob. */
export async function generateSummaryPdfBlob(opts: {
  lang: Lang;
  profile: UserProfile | null;
  careerPathLabel: string;
  date: string;
  durationLabel?: string;
  systems: AppIntegration[];
  qa: QAItem[];
  checklist: ChecklistItem[];
  pendingIds?: string[];
}): Promise<Blob | null> {
  try {
    const rtl = opts.lang === "ar";
    const blocks = buildBlocks(opts);
    const [qrImages, logo] = await Promise.all([
      preloadQrImages(blocks),
      loadImage("/logo_dark.svg").catch(() => null),
    ]);

    const measureCtx = newPageCanvas().ctx;
    measureCtx.direction = rtl ? "rtl" : "ltr";

    const pages: HTMLCanvasElement[] = [];
    const pageCtxs: CanvasRenderingContext2D[] = [];
    const pageLinks: LinkRegion[][] = [];
    let currentLinks: LinkRegion[] = [];
    let { canvas, ctx } = newPageCanvas();
    let y = TOP_START;

    for (const block of blocks) {
      const h = measureBlock(measureCtx, block, qrImages);
      if (y + h > BOTTOM_LIMIT && y > TOP_START) {
        pages.push(canvas);
        pageCtxs.push(ctx);
        pageLinks.push(currentLinks);
        currentLinks = [];
        ({ canvas, ctx } = newPageCanvas());
        y = TOP_START;
      }
      const result = drawBlock(ctx, block, y, rtl, qrImages);
      y = result.y;
      currentLinks.push(...result.links);
    }
    pages.push(canvas);
    pageCtxs.push(ctx);
    pageLinks.push(currentLinks);

    const generatedOn = opts.date;
    pages.forEach((_, i) => {
      drawChrome(pageCtxs[i], { lang: opts.lang, logo, pageIndex: i, pageCount: pages.length, generatedOn });
    });

    const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
    const pageWidthPt = doc.internal.pageSize.getWidth();
    const pageHeightPt = doc.internal.pageSize.getHeight();
    const scaleX = pageWidthPt / PAGE_W;
    const scaleY = pageHeightPt / PAGE_H;
    pages.forEach((pg, i) => {
      if (i > 0) doc.addPage();
      const dataUrl = pg.toDataURL("image/jpeg", 0.92);
      doc.addImage(dataUrl, "JPEG", 0, 0, pageWidthPt, pageHeightPt);
      for (const link of pageLinks[i] || []) {
        doc.link(link.x * scaleX, link.y * scaleY, link.w * scaleX, link.h * scaleY, { url: link.url });
      }
    });

    return doc.output("blob");
  } catch (err) {
    console.warn("[summaryPdf] generation failed", err);
    return null;
  }
}

/** Trigger a browser download of the summary PDF. */
export async function downloadSummaryPdf(opts: Parameters<typeof generateSummaryPdfBlob>[0]): Promise<void> {
  const blob = await generateSummaryPdfBlob(opts);
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Kaden-Onboarding-Summary.pdf";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 15000);
}
